import { useState, useEffect, useCallback, useRef } from 'react';
import { fetchMyNotifications } from '../services/notificationService';

const BADGE_KEY = 'login_popup_badge';

/**
 * Manages the login-time notification popup.
 * - On every login, fetches unread notifications and shows popup if any exist.
 * - If dismissed without action, keeps a persistent badge flag in sessionStorage.
 * - Badge clears when user takes action.
 */
const useLoginNotifications = (userId) => {
  const [showPopup, setShowPopup] = useState(false);
  const [pendingNotifications, setPendingNotifications] = useState([]);
  const [hasPersistentBadge, setHasPersistentBadge] = useState(false);
  const [loading, setLoading] = useState(false);
  const checkedRef = useRef(false);

  const checkPendingOnLogin = useCallback(async () => {
    if (!userId || checkedRef?.current) return;
    checkedRef.current = true;

    // Restore badge if previously dismissed without acting
    const badgeActive = sessionStorage.getItem(BADGE_KEY);
    if (badgeActive === 'true') setHasPersistentBadge(true);

    setLoading(true);
    try {
      const notifications = await fetchMyNotifications({ limit: 50, unreadOnly: true });
      if (notifications?.length > 0) {
        setPendingNotifications(notifications);
        setShowPopup(true);
      }
    } catch (err) {
      console.warn('[LoginNotifications] Check error:', err?.message);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (userId) {
      // Small delay to let the app settle after login
      const timer = setTimeout(checkPendingOnLogin, 1200);
      return () => clearTimeout(timer);
    }
  }, [userId, checkPendingOnLogin]);

  // Called when user clicks "Go to Pending Tasks" — clears badge
  const handleAction = useCallback(() => {
    setShowPopup(false);
    setHasPersistentBadge(false);
    sessionStorage.removeItem(BADGE_KEY);
  }, []);

  // Called when user dismisses popup without acting
  const handleDismiss = useCallback(() => {
    setShowPopup(false);
    if (pendingNotifications?.length > 0) {
      setHasPersistentBadge(true);
      sessionStorage.setItem(BADGE_KEY, 'true');
    }
  }, [pendingNotifications]);

  // Called externally when all notifications are cleared/read
  const clearPersistentBadge = useCallback(() => {
    setHasPersistentBadge(false);
    sessionStorage.removeItem(BADGE_KEY);
  }, []);

  return {
    showPopup,
    pendingNotifications,
    hasPersistentBadge,
    loading,
    handleAction,
    handleDismiss,
    clearPersistentBadge,
  };
};

export default useLoginNotifications;
